import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Trailers, played so they look like Zflix rather than a YouTube page (Zach, 2026-11-03): YouTube's player with all
 * of its own buttons off, under Zflix's title and progress bar, which also cover where YouTube puts its title and
 * logo. Taps and the remote go to Zflix (OK pause, Left/Right 10 s, Back closes). It closes just before the end, so
 * YouTube's "more videos" screen never shows.
 */
let apiReady = null;
function loadApi() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (apiReady) return apiReady;
  apiReady = new Promise((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve();
    };
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    s.onerror = () => {
      apiReady = null;
      reject(new Error('Trailer could not load'));
    };
    document.head.appendChild(s);
  });
  return apiReady;
}

/** { key, name } for this title, or null when there is none. */
export function useTrailer(item) {
  const [t, setT] = useState(null);
  const id = item?.source === 'jellyfin' ? item.id : '';
  const tmdbId = item?.tmdbId || '';
  const type = item?.type === 'movie' ? 'movie' : 'show';
  useEffect(() => {
    setT(null);
    if (!id && !tmdbId) return undefined;
    let dead = false;
    const q = id ? `id=${encodeURIComponent(id)}` : `tmdbId=${encodeURIComponent(tmdbId)}&type=${type}`;
    fetch(`/api/trailer?${q}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => !dead && setT(j && j.key ? j : null))
      .catch(() => {});
    return () => {
      dead = true;
    };
  }, [id, tmdbId, type]);
  return t;
}

/**
 * Trailers play with no captions (Zach, 2026-11-03: they all still had them). YouTube turns them on by itself for many
 * videos once playback starts; clearing the track and unloading the captions module turns them off.
 */
function captionsOff(p) {
  try {
    p.setOption('captions', 'track', {});
  } catch (_) {}
  try {
    p.setOption('cc', 'track', {});
  } catch (_) {}
  try {
    p.unloadModule('captions');
  } catch (_) {}
  try {
    p.unloadModule('cc');
  } catch (_) {}
}

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export function TrailerPlayer({ trailer, title, onClose }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [t, setT] = useState({ at: 0, dur: 0, paused: false, ready: false });
  const [error, setError] = useState('');

  useEffect(() => {
    let dead = false;
    let poll = null;
    loadApi()
      .then(() => {
        if (dead) return;
        playerRef.current = new window.YT.Player(hostRef.current, {
          host: 'https://www.youtube-nocookie.com',
          videoId: trailer.key,
          width: '100%',
          height: '100%',
          playerVars: { autoplay: 1, controls: 0, disablekb: 1, fs: 0, iv_load_policy: 3, modestbranding: 1, rel: 0, playsinline: 1, cc_load_policy: 0, origin: window.location.origin },
          events: {
            onReady: (e) => {
              captionsOff(e.target);
              e.target.playVideo();
              poll = setInterval(() => {
                const p = playerRef.current;
                if (!p || !p.getDuration) return;
                const dur = p.getDuration() || 0;
                const at = p.getCurrentTime() || 0;
                setT({ at, dur, paused: p.getPlayerState() === 2, ready: true });
                /** The first 8 seconds: if captions came back on, off again. */
                if (at < 8) {
                  try {
                    const tr = p.getOption && p.getOption('captions', 'track');
                    if (tr && tr.languageCode) captionsOff(p);
                  } catch (_) {}
                }
                /** Gone before YouTube's end screen of other videos. */
                if (dur > 5 && dur - at < 0.6) closeRef.current();
              }, 250);
            },
            onStateChange: (e) => {
              if (e.data === 0) closeRef.current();
              /** Playing: YouTube loads its captions only now, so they come off now (and once more just after). */
              if (e.data === 1) {
                captionsOff(e.target);
                setTimeout(() => captionsOff(e.target), 800);
                setTimeout(() => captionsOff(e.target), 2500);
              }
            },
            onApiChange: (e) => captionsOff(e.target),
            onError: () => setError('This trailer cannot be played here.'),
          },
        });
      })
      .catch((e) => setError(e.message));
    return () => {
      dead = true;
      clearInterval(poll);
      try {
        playerRef.current?.destroy();
      } catch (_) {}
    };
  }, [trailer.key]);

  const toggle = () => {
    const p = playerRef.current;
    if (!p || !p.getPlayerState) return;
    if (p.getPlayerState() === 1) p.pauseVideo();
    else p.playVideo();
  };
  const jump = (d) => {
    const p = playerRef.current;
    if (p && p.getCurrentTime) p.seekTo(Math.max(0, p.getCurrentTime() + d), true);
  };

  /** The remote: tvSpatialNav hands every key here first while the trailer is up (like Up next). */
  useEffect(() => {
    const prevKeys = window.__zflixTopKeys;
    const prevBack = window.__zflixOwnBack;
    window.__zflixTopKeys = (e) => {
      const k = e.key;
      if (k === 'Enter' || k === ' ' || k === 'MediaPlayPause') toggle();
      else if (k === 'ArrowLeft' || k === 'MediaRewind') jump(-10);
      else if (k === 'ArrowRight' || k === 'MediaFastForward') jump(10);
      else if (k === 'Escape' || k === 'GoBack' || k === 'BrowserBack' || k === 'Backspace') closeRef.current();
      return true;
    };
    window.__zflixOwnBack = () => {
      closeRef.current();
      return true;
    };
    return () => {
      if (prevKeys) window.__zflixTopKeys = prevKeys;
      else delete window.__zflixTopKeys;
      if (prevBack) window.__zflixOwnBack = prevBack;
      else delete window.__zflixOwnBack;
    };
  }, []);

  const pct = t.dur ? Math.min(100, (t.at / t.dur) * 100) : 0;
  return ReactDOM.createPortal(
    <div data-zflix-trailer style={{ position: 'fixed', inset: 0, zIndex: 100002, background: '#000', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div ref={hostRef} style={{ width: '100%', height: '100%' }} />
      </div>
      {/* Everything YouTube would show is under this: taps reach Zflix, not YouTube. */}
      <div onClick={toggle} style={{ position: 'absolute', inset: 0, cursor: 'pointer' }} />
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 120, background: 'linear-gradient(to bottom, rgba(0,0,0,0.95), rgba(0,0,0,0.75) 45%, transparent)', display: 'flex', alignItems: 'flex-start', padding: '22px 28px', pointerEvents: 'none' }}>
        <div style={{ color: '#fff', minWidth: 0 }}>
          <div style={{ fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: '#e50914', fontWeight: 800 }}>Trailer</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        </div>
      </div>
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close trailer" style={{ position: 'absolute', top: 18, right: 22, width: 44, height: 44, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 22, cursor: 'pointer' }}>✕</button>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 110, background: 'linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.75) 50%, transparent)', padding: '0 28px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pointerEvents: 'none' }}>
        <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.25)', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: '#e50914' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, color: '#ddd', fontSize: 13 }}>
          <span>{t.paused ? 'Paused' : ''}</span>
          <span>{t.dur ? `${fmt(t.at)} / ${fmt(t.dur)}` : ''}</span>
        </div>
      </div>
      {!t.ready && !error ? (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <style>{'@keyframes zflix-spin{to{transform:rotate(360deg)}}'}</style>
          <div style={{ width: 56, height: 56, borderRadius: '50%', border: '5px solid rgba(255,255,255,0.22)', borderTopColor: '#e50914', animation: 'zflix-spin 0.9s linear infinite' }} />
        </div>
      ) : null}
      {error ? (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, pointerEvents: 'none' }}>{error}</div>
      ) : null}
    </div>,
    document.body,
  );
}
